import React from 'react'
import { formatDate } from '../../utils/api'
import { getStageName } from '../../utils/dealConstants'
import AppLink from '../shared/AppLink.jsx'
import { orderKey } from './caseOrder.js'
import { printerAnswerLabel } from '../../utils/returnPrinter.js'

/**
 * Customer-context view — pinned items, customer history (deals/orders),
 * previous tickets, case / return-request panel, and internal notes.
 *
 * State lives in the orchestrator (Tickets.jsx) and is passed in via
 * props. JSX is moved verbatim from the orchestrator — desktop visual
 * must be byte-identical.
 */
function PhoneContextStack({
  ticketDetails, customerHistory, notes,
  newNote, setNewNote, savingNote, addNote,
  selectTicket, openSidebarDeal, openSidebarOrder, setCustomerEmail,
  compact = false
}) {
  const ticket = ticketDetails?.ticket || {}
  const displayName = ticket.customer_name_extracted || ticket.from_name || ticket.from_email || ticket.customer_email || 'Unknown'
  const email = ticket.from_email || ticket.customer_email || ''
  const initials = displayName.split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?'

  const orders = customerHistory?.orders || []
  const deals = customerHistory?.deals || []
  const previousTickets = customerHistory?.previousTickets || []
  const ltv = orders.reduce((sum, o) => sum + (parseFloat(o.total) || 0), 0)

  const linkedItems = [
    ...orders.slice(0, 3).map(o => ({
      key: `o-${o.id}`,
      title: o.id?.startsWith('#') ? o.id : `#${o.id}`,
      meta: `Order · ${o.platform || 'Shopify'} · £${(parseFloat(o.total) || 0).toFixed(2)}`,
      onClick: () => openSidebarOrder?.(o)
    })),
    ...deals.slice(0, 3).map(d => ({
      key: `d-${d.id}`,
      title: d.name || 'Unnamed Deal',
      meta: `Deal · ${getStageName(d.stage)}`,
      onClick: () => openSidebarDeal?.(d)
    }))
  ]
  const topTickets = previousTickets.slice(0, 3)
  const [showAddNote, setShowAddNote] = React.useState(false)

  return (
    <div className={`ctx--phone${compact ? ' ctx--compact' : ''}`}>
      {/* Customer card */}
      <div className="card">
        <div className="cust">
          <div className="av">{initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="cust-name" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayName}</div>
            {email && (
              <button
                className="cust-email"
                onClick={() => setCustomerEmail?.(email)}
                style={{ background: 'transparent', border: 0, padding: 0, color: '#6b7280', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', textAlign: 'left' }}
              >
                {email}
              </button>
            )}
          </div>
        </div>
        <div className="cust-stats">
          <div className="stat"><div className="v">£{ltv.toFixed(0)}</div><div className="l">LTV</div></div>
          <div className="stat"><div className="v">{orders.length}</div><div className="l">Orders</div></div>
          <div className="stat"><div className="v">{previousTickets.length + 1}</div><div className="l">Tickets</div></div>
        </div>
      </div>

      {/* Linked items */}
      {linkedItems.length > 0 && (
        <div className="card">
          <div className="card-h"><span>Linked Items</span></div>
          {linkedItems.map(item => (
            <div key={item.key} className="li" onClick={item.onClick}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="li-title" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.title}</div>
                <div className="li-meta">{item.meta}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Previous tickets */}
      {previousTickets.length > 0 && (
        <div className="card">
          <div className="card-h">
            <span>Previous Tickets</span>
            {previousTickets.length > 3 && (
              <span className="more" onClick={() => setCustomerEmail?.(email)}>
                See all ({previousTickets.length})
              </span>
            )}
          </div>
          {topTickets.map(t => (
            <div key={t.id} className="li" onClick={() => selectTicket?.(t)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="li-title" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.subject || '(No subject)'}</div>
                <div className="li-meta">{t.status}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Notes */}
      <div className="card">
        <div className="card-h"><span>Notes</span></div>
        {notes.length === 0 && !showAddNote && (
          <div className="li-meta" style={{ padding: '4px 0 8px' }}>No notes yet</div>
        )}
        {notes.map(note => (
          <div key={note.id} style={{ fontSize: 13, padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
            <div>{note.content}</div>
            <div className="li-meta" style={{ marginTop: 2 }}>{note.author_name || 'Staff'} · {formatDate(note.created_at)}</div>
          </div>
        ))}
        {showAddNote ? (
          <div className="add-note-form">
            <textarea
              placeholder="Add a note (staff only)…"
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
              autoFocus
            />
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                className="comp-cancel"
                onClick={() => { setShowAddNote(false); setNewNote('') }}
                style={{ flex: 1 }}
              >Cancel</button>
              <button
                className="comp-send"
                onClick={async () => { await addNote?.(); setShowAddNote(false) }}
                disabled={!newNote.trim() || savingNote}
                style={{ flex: 1 }}
              >{savingNote ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        ) : (
          <button className="add-note" onClick={() => setShowAddNote(true)}>+ Add note</button>
        )}
      </div>
    </div>
  )
}

function TicketContext({
  // Layout / phone mode
  breakpoint, mobileV2 = false,
  // Visibility / sizing
  isMobile, mobileView, rightPanelWidth,
  // Ticket gating
  selectedTicket, ticketDetails,
  // History / pins
  customerHistory, pinnedItems, togglingPin, loadingHistory, loadingSidebarOrder,
  // Notes
  notes, newNote, setNewNote, savingNote,
  // Case / return-request
  returnRequest, returnReasons, showReturnForm, setShowReturnForm,
  returnFormData, setReturnFormData, returnLoading,
  override30DayCase, setOverride30DayCase,
  caseLines, caseLineRefs, setCaseLineRefs, setCaseOrderId,
  // Handlers
  setCustomerEmail,
  openSidebarDeal, openSidebarOrder, toggleSidebarPin,
  selectTicket, getStateDisplay, getCaseStatusBadge,
  findChannelOrder, createReturnRequest, addNote
}) {
  // The orchestrator previously gated the entire sidebar on
  // selectedTicket && ticketDetails — preserve that.
  if (!selectedTicket || !ticketDetails) return null

  // Reuse the card-stack treatment for both phone and tablet. Tablet
  // gets `compact` so the cards squeeze into the narrow column.
  const isStack = (breakpoint === 'phone' || breakpoint === 'tablet') && mobileV2

  if (isStack) {
    return (
      <PhoneContextStack
        compact={breakpoint === 'tablet'}
        ticketDetails={ticketDetails}
        customerHistory={customerHistory}
        notes={notes}
        newNote={newNote}
        setNewNote={setNewNote}
        savingNote={savingNote}
        addNote={addNote}
        selectTicket={selectTicket}
        openSidebarDeal={openSidebarDeal}
        openSidebarOrder={openSidebarOrder}
        setCustomerEmail={setCustomerEmail}
      />
    )
  }

  return (
    <aside
      className="rightSidebarColumn"
      style={{
        display: isMobile && mobileView !== 'sidebar' ? 'none' : 'flex',
        width: isMobile ? '100%' : rightPanelWidth
      }}
    >
      {/* Pinned Items */}
      {customerHistory && Object.keys(pinnedItems).length > 0 ? (
        <div className="card" style={{ padding: 0, borderLeft: '3px solid #f59e0b' }}>
          <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(0,0,0,.06)', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, color: '#92400e' }}>
            <span style={{ fontSize: 14 }}>{'📌'}</span>
            <span>Pinned ({Object.keys(pinnedItems).length})</span>
          </div>
          <div style={{ padding: 8, maxHeight: 180, overflowY: 'auto' }}>
            {customerHistory.deals?.filter(d => pinnedItems[`deal:${d.id}`]).map(d => (
              <div
                key={`pin-deal-${d.id}`}
                style={{ fontSize: 12, marginBottom: 4, padding: '6px 8px', background: '#fffbeb', borderRadius: 4, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                onClick={() => openSidebarDeal(d)}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name || 'Unnamed Deal'}</div>
                  <div style={{ fontSize: 10, color: '#92400e' }}>Deal &middot; {getStageName(d.stage)}</div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); toggleSidebarPin('deal', d.id) }}
                  title="Unpin"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: 13, color: '#dc2626' }}
                >{'📌'}</button>
              </div>
            ))}
            {customerHistory.orders?.filter(o => pinnedItems[`order:${o.shopifyId || o.id}`]).map(o => {
              const displayId = o.id?.startsWith('#') ? o.id : `#${o.id}`
              return (
                <div
                  key={`pin-order-${o.id}`}
                  style={{ fontSize: 12, marginBottom: 4, padding: '6px 8px', background: '#fffbeb', borderRadius: 4, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  onClick={() => openSidebarOrder(o)}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500 }}>{displayId} &middot; £{parseFloat(o.total || 0).toFixed(2)}</div>
                    <div style={{ fontSize: 10, color: '#92400e' }}>Order &middot; {o.platform}</div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleSidebarPin('order', o.shopifyId || o.id) }}
                    title="Unpin"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: 13, color: '#dc2626' }}
                  >{'📌'}</button>
                </div>
              )
            })}
            {customerHistory.previousTickets?.filter(t => pinnedItems[`ticket:${t.id}`]).map(t => (
              <div
                key={`pin-ticket-${t.id}`}
                style={{ fontSize: 12, marginBottom: 4, padding: '6px 8px', background: '#fffbeb', borderRadius: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject || 'No subject'}</div>
                  <div style={{ fontSize: 10, color: '#92400e' }}>Ticket &middot; {t.status}</div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); toggleSidebarPin('ticket', t.id) }}
                  title="Unpin"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: 13, color: '#dc2626' }}
                >{'📌'}</button>
              </div>
            ))}
          </div>
        </div>
      ) : customerHistory ? (
        <div className="card" style={{ padding: 0, opacity: 0.6 }}>
          <div style={{ padding: '10px 16px', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--muted)' }}>
            <span style={{ fontSize: 14 }}>{'📌'}</span>
            <span>Pinned</span>
          </div>
          <div style={{ padding: '8px 16px 12px', fontSize: 11, color: 'var(--muted)', textAlign: 'center' }}>No pinned items</div>
        </div>
      ) : null}

      {/* Customer History (Deals & Orders) */}
      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(0,0,0,.06)', fontWeight: 600, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Customer History</span>
          <button
            className="btn btn-sm"
            style={{ color: '#2563eb', background: 'transparent', border: 'none', fontSize: 11, padding: '4px 8px' }}
            onClick={() => setCustomerEmail(ticketDetails.ticket?.from_email || ticketDetails.ticket?.customer_email)}
            title="View full customer profile"
          >
            View All
          </button>
        </div>
        <div style={{ padding: 12 }}>
          {loadingHistory ? (
            <div className="loading-spinner" style={{ margin: '16px auto', width: 20, height: 20 }} />
          ) : customerHistory ? (
            <>
              {/* Deals */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', marginBottom: 6, textTransform: 'uppercase' }}>
                  Deals ({customerHistory.deals?.length || 0})
                </div>
                {customerHistory.deals?.length > 0 ? (
                  <>
                    {customerHistory.deals.slice(0, 5).map(d => (
                      <div
                        key={d.id}
                        style={{ fontSize: 12, marginBottom: 6, padding: 8, background: 'var(--bg)', borderRadius: 4, cursor: 'pointer' }}
                        onClick={() => openSidebarDeal(d)}
                      >
                        <div style={{ fontWeight: 500, marginBottom: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name || 'Unnamed Deal'}</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleSidebarPin('deal', d.id) }}
                            disabled={togglingPin === `deal:${d.id}`}
                            title={pinnedItems[`deal:${d.id}`] ? 'Unpin deal' : 'Pin deal'}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: 14, lineHeight: 1, opacity: pinnedItems[`deal:${d.id}`] ? 1 : 0.3, color: pinnedItems[`deal:${d.id}`] ? '#dc2626' : 'var(--muted)', transition: 'opacity 0.15s' }}
                          >{'📌'}</button>
                        </div>
                        <div style={{ color: 'var(--muted)', display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                          <span>{getStageName(d.stage)}</span>
                          {d.amount && <span style={{ fontWeight: 600 }}>£{parseFloat(d.amount).toFixed(2)}</span>}
                        </div>
                      </div>
                    ))}
                    {customerHistory.deals.length > 5 && (
                      <button
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          fontSize: 11,
                          color: '#2563eb',
                          background: 'transparent',
                          border: '1px dashed #e5e7eb',
                          borderRadius: 4,
                          cursor: 'pointer',
                          marginTop: 4
                        }}
                        onClick={() => window.open(`/deals/all?email=${encodeURIComponent(ticketDetails?.ticket?.from_email || ticketDetails?.ticket?.customer_email || '')}`, '_blank')}
                      >
                        View all {customerHistory.deals.length} deals...
                      </button>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 11, color: 'var(--muted)', padding: 8, textAlign: 'center' }}>No deals found</div>
                )}
              </div>

              {/* Orders */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', marginBottom: 6, textTransform: 'uppercase' }}>
                  Orders ({customerHistory.orders?.length || 0})
                </div>
                {customerHistory.orders?.length > 0 ? (
                  <>
                    {customerHistory.orders.slice(0, 5).map(o => {
                      const displayId = o.id?.startsWith('#') ? o.id : `#${o.id}`
                      const orderEntityId = o.shopifyId || o.id
                      return (
                        <div
                          key={o.id}
                          style={{ fontSize: 12, marginBottom: 6, padding: 8, background: 'var(--bg)', borderRadius: 4, cursor: 'pointer', opacity: loadingSidebarOrder === o.shopifyId ? 0.6 : 1 }}
                          onClick={() => openSidebarOrder(o)}
                        >
                          <div style={{ fontWeight: 500, marginBottom: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>{displayId}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span style={{ fontWeight: 600 }}>£{parseFloat(o.total || 0).toFixed(2)}</span>
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleSidebarPin('order', orderEntityId) }}
                                disabled={togglingPin === `order:${orderEntityId}`}
                                title={pinnedItems[`order:${orderEntityId}`] ? 'Unpin order' : 'Pin order'}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: 14, lineHeight: 1, opacity: pinnedItems[`order:${orderEntityId}`] ? 1 : 0.3, color: pinnedItems[`order:${orderEntityId}`] ? '#dc2626' : 'var(--muted)', transition: 'opacity 0.15s' }}
                              >{'📌'}</button>
                            </div>
                          </div>
                          <div style={{ color: 'var(--muted)', display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                            <span>{o.platform}</span>
                            <span>{loadingSidebarOrder === o.shopifyId ? 'Loading...' : o.status}</span>
                          </div>
                        </div>
                      )
                    })}
                    {customerHistory.orders.length > 5 && (
                      <button
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          fontSize: 11,
                          color: '#2563eb',
                          background: 'transparent',
                          border: '1px dashed #e5e7eb',
                          borderRadius: 4,
                          cursor: 'pointer',
                          marginTop: 4
                        }}
                        onClick={() => window.open(`/orders?email=${encodeURIComponent(ticketDetails?.ticket?.from_email || ticketDetails?.ticket?.customer_email || '')}`, '_blank')}
                      >
                        View all {customerHistory.orders.length} orders...
                      </button>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 11, color: 'var(--muted)', padding: 8, textAlign: 'center' }}>No orders found</div>
                )}
              </div>
            </>
          ) : (
            <div style={{ fontSize: 11, color: 'var(--muted)', padding: 8, textAlign: 'center' }}>No history available</div>
          )}
        </div>
      </div>

      {/* Customer Tickets */}
      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(0,0,0,.06)', fontWeight: 600, fontSize: 13 }}>
          Customer Tickets ({customerHistory?.previousTickets?.length || 0})
        </div>
        <div style={{ padding: 12, maxHeight: 200, overflowY: 'auto' }}>
          {loadingHistory ? (
            <div className="loading-spinner" style={{ margin: '16px auto', width: 20, height: 20 }} />
          ) : customerHistory?.previousTickets?.length > 0 ? (
            customerHistory.previousTickets.map(t => (
              <div
                key={t.id}
                style={{ fontSize: 12, marginBottom: 6, padding: 8, background: 'var(--bg)', borderRadius: 4, cursor: 'pointer' }}
                onClick={() => selectTicket(t)}
              >
                <div style={{ fontWeight: 500, marginBottom: 4, lineHeight: 1.3 }}>{t.subject || '(No subject)'}</div>
                <div style={{ color: 'var(--muted)', display: 'flex', gap: 8, alignItems: 'center', fontSize: 11 }}>
                  <span className={`badge badge-${t.status === 'resolved' ? 'success' : (t.status === 'new' || t.status === 'awaiting_reply') ? 'warning' : 'secondary'}`} style={{ fontSize: 9, padding: '2px 6px' }}>
                    {getStateDisplay(t.status).label}
                  </span>
                  <span>{formatDate(t.created_at)}</span>
                </div>
              </div>
            ))
          ) : (
            <div style={{ fontSize: 11, color: 'var(--muted)', padding: 8, textAlign: 'center' }}>No previous tickets</div>
          )}
        </div>
      </div>

      {/* Case */}
      {ticketDetails?.ticket && (
        <div className="card" style={{ padding: 0 }}>
          <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(0,0,0,.06)', fontWeight: 600, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: '#dc2626' }}>&#9888;</span> Case
            </span>
            {returnRequest && getCaseStatusBadge(returnRequest.status)}
          </div>
          <div style={{ padding: 12 }}>
            {/* No case yet - show Open Case button */}
            {!returnRequest && !showReturnForm && (
              <button
                className="btn btn-sm"
                style={{ width: '100%', background: '#dc2626', color: '#fff', border: 'none', padding: '8px 12px', borderRadius: 6, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
                onClick={() => setShowReturnForm(true)}
              >
                Open Case
              </button>
            )}

            {/* Case form - pick reason + description */}
            {!returnRequest && showReturnForm && (
              <div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>What is the reason?</label>
                  <select
                    className="form-input"
                    value={returnFormData.reason_id}
                    onChange={e => setReturnFormData(prev => ({ ...prev, reason_id: e.target.value }))}
                    style={{ fontSize: 12, width: '100%' }}
                  >
                    <option value="">Select reason...</option>
                    {(() => {
                      const cancellationNames = ['Pre-Shipment Cancellation']
                      const generalNames = ['Changed My Mind', 'Not What I Expected', '30-Day Money Back Guarantee', 'Ordered By Mistake']
                      const cancellations = returnReasons.filter(r => cancellationNames.includes(r.name))
                      const general = returnReasons.filter(r => !r.requires_troubleshooting && generalNames.includes(r.name))
                      const nonTechFault = returnReasons.filter(r => !r.requires_troubleshooting && !generalNames.includes(r.name) && !cancellationNames.includes(r.name))
                      const technical = returnReasons.filter(r => r.requires_troubleshooting)
                      return (
                        <>
                          <optgroup label="Cancellations">
                            {cancellations.map(r => (
                              <option key={r.id} value={r.id}>{r.name}</option>
                            ))}
                          </optgroup>
                          <optgroup label="General Returns (30-day guarantee)">
                            {general.map(r => (
                              <option key={r.id} value={r.id}>{r.name}</option>
                            ))}
                          </optgroup>
                          <optgroup label="Non-Technical Issues">
                            {nonTechFault.map(r => (
                              <option key={r.id} value={r.id}>{r.name}</option>
                            ))}
                          </optgroup>
                          <optgroup label="Technical Issues">
                            {technical.map(r => (
                              <option key={r.id} value={r.id}>{r.name}</option>
                            ))}
                          </optgroup>
                        </>
                      )
                    })()}
                  </select>
                </div>
                {returnFormData.reason_id && (() => {
                  const selectedReason = returnReasons.find(r => r.id === parseInt(returnFormData.reason_id));
                  if (!selectedReason) return null;
                  const isTechnical = selectedReason.requires_troubleshooting;
                  const cancellationNames = ['Pre-Shipment Cancellation']
                  const generalNames = ['Changed My Mind', 'Not What I Expected', '30-Day Money Back Guarantee', 'Ordered By Mistake']
                  const isCancellation = cancellationNames.includes(selectedReason.name)
                  const isGeneral = !isTechnical && !isCancellation && generalNames.includes(selectedReason.name)
                  const bg = isCancellation ? '#fef3c7' : isTechnical ? '#eff6ff' : isGeneral ? '#fefce8' : '#f0fdf4'
                  const border = isCancellation ? '#f59e0b' : isTechnical ? '#bfdbfe' : isGeneral ? '#fde68a' : '#bbf7d0'
                  const color = isCancellation ? '#92400e' : isTechnical ? '#1e40af' : isGeneral ? '#854d0e' : '#166534'
                  const label = isCancellation ? 'Pre-Shipment Cancellation' : isTechnical ? 'Technical Issue' : isGeneral ? '30-Day Guarantee Return' : 'Non-Technical Issue'
                  const desc = isCancellation
                    ? 'Case opened → Reviewer will cancel order and process refund'
                    : isTechnical
                      ? 'Case opened → Reviewer may request evidence & troubleshooting'
                      : 'Case opened → Reviewer will assess and decide next steps'
                  return (
                    <div style={{
                      margin: '0 0 8px', padding: '8px 12px', borderRadius: 6, fontSize: 11, lineHeight: 1.5,
                      background: bg, border: `1px solid ${border}`, color
                    }}>
                      <div style={{ fontWeight: 700, marginBottom: 2 }}>{label}</div>
                      <div>{desc}</div>
                    </div>
                  );
                })()}
                <CaseOrderPicker
                  orders={customerHistory?.orders || []}
                  selectedOrder={findChannelOrder()}
                  onChange={setCaseOrderId}
                />
                <CaseItemPicker lines={caseLines} refs={caseLineRefs} setRefs={setCaseLineRefs} />
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Description</label>
                  <textarea
                    className="form-input"
                    placeholder="Describe the issue..."
                    value={returnFormData.reason_notes}
                    onChange={e => setReturnFormData(prev => ({ ...prev, reason_notes: e.target.value }))}
                    rows={3}
                    style={{ fontSize: 12, width: '100%' }}
                  />
                </div>
                {(() => {
                  const order = findChannelOrder()
                  const orderDateStr = order?.created || order?.createdAt
                  if (!orderDateStr) return null
                  const daysSince = Math.floor((Date.now() - new Date(orderDateStr).getTime()) / (1000 * 60 * 60 * 24))
                  if (daysSince <= 30) return null
                  return (
                    <div style={{ padding: '8px 10px', marginBottom: 8, borderRadius: 6, background: '#fef3c7', border: '1px solid #fde68a' }}>
                      <div style={{ fontSize: 11, color: '#92400e', lineHeight: 1.4, marginBottom: 6 }}>
                        <strong>Outside 30-day guarantee.</strong> This order was placed {daysSince} days ago. Returns and refunds are not available by default.
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 11 }}>
                        <input
                          type="checkbox"
                          checked={override30DayCase}
                          onChange={e => setOverride30DayCase(e.target.checked)}
                          style={{ width: 14, height: 14 }}
                        />
                        <span style={{ fontWeight: 600, color: '#92400e' }}>Override — proceed anyway</span>
                      </label>
                    </div>
                  )
                })()}
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    className="btn btn-sm"
                    style={{ flex: 1, background: '#dc2626', color: '#fff', border: 'none', fontWeight: 600, fontSize: 11 }}
                    onClick={createReturnRequest}
                    disabled={!returnFormData.reason_id || !returnFormData.reason_notes?.trim() || returnLoading || ((caseLines?.length || 0) > 1 && !caseLineRefs.length) || (() => {
                      const order = findChannelOrder()
                      const orderDateStr = order?.created || order?.createdAt
                      if (!orderDateStr) return false
                      const daysSince = Math.floor((Date.now() - new Date(orderDateStr).getTime()) / (1000 * 60 * 60 * 24))
                      return daysSince > 30 && !override30DayCase
                    })()}
                  >
                    {returnLoading ? 'Submitting...' : 'Submit'}
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    style={{ fontSize: 11 }}
                    onClick={() => { setShowReturnForm(false); setReturnFormData({ reason_id: '', reason_notes: '' }); setOverride30DayCase(false) }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Case exists - show See Case button */}
            {returnRequest && (
              <div>
                <AppLink
                  href={`/orders/cases?caseId=${returnRequest.id}`}
                  newTab
                  className="btn btn-sm see-case-link"
                >
                  See Case {returnRequest.public_id || '#' + returnRequest.id}
                </AppLink>
                <div style={{ marginTop: 6, fontSize: 11, color: 'var(--muted)', textAlign: 'center' }}>
                  {returnRequest.reason_name} &middot; {formatDate(returnRequest.created_at)}
                </div>
                {printerAnswerLabel(returnRequest.has_printer) && (
                  <div style={{ marginTop: 4, fontSize: 11, color: 'var(--muted)', textAlign: 'center' }}>
                    Customer answer: {printerAnswerLabel(returnRequest.has_printer)}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Internal Notes */}
      <div className="card" style={{ padding: 0, flex: 1 }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,.06)', fontWeight: 600 }}>
          Internal Notes
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ marginBottom: 12 }}>
            <textarea
              className="form-input"
              placeholder="Add a note (staff only)..."
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
              rows={2}
              style={{ marginBottom: 8, fontSize: 12 }}
            />
            <button
              className="btn btn-sm btn-secondary"
              onClick={addNote}
              disabled={!newNote.trim() || savingNote}
              style={{ width: '100%' }}
            >
              {savingNote ? 'Saving...' : 'Add Note'}
            </button>
          </div>

          {notes.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', padding: 16 }}>
              No notes yet
            </div>
          ) : (
            <div>
              {notes.map(note => (
                <div key={note.id} style={{ fontSize: 12, marginBottom: 12, padding: 8, background: '#fff3cd', borderRadius: 4 }}>
                  <div style={{ marginBottom: 4 }}>{note.content}</div>
                  <div style={{ color: 'var(--muted)', fontSize: 11 }}>
                    {note.author_name || 'Staff'} • {formatDate(note.created_at)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </aside>
  )
}

export default TicketContext

/**
 * Which item(s) the case is about (spec 20261008). A single-line order shows its
 * item and needs no tick. Shows nothing when the order's lines are not known
 * (the refund box then starts on Refund all).
 */
// Shown only when the customer has more than one order to choose from.
function CaseOrderPicker({ orders, selectedOrder, onChange }) {
  if (orders.length < 2) return null
  return (
    <div style={{ marginBottom: 8 }}>
      <label htmlFor="case-order" style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Which order is this for?</label>
      <select
        id="case-order"
        className="form-input"
        value={selectedOrder ? orderKey(selectedOrder) : ''}
        onChange={e => onChange(e.target.value)}
        style={{ fontSize: 12, width: '100%' }}
      >
        {!selectedOrder && <option value="" disabled>Select order...</option>}
        {orders.map(o => (
          <option key={orderKey(o)} value={orderKey(o)}>
            {`${o.id?.startsWith('#') ? o.id : `#${o.id}`} · ${formatDate(o.created || o.createdAt)} · ${o.platform || 'Shopify'}`}
          </option>
        ))}
      </select>
    </div>
  )
}

function CaseItemPicker({ lines, refs, setRefs }) {
  if (!lines || lines.length === 0) return null
  if (lines.length === 1) {
    return (
      <div style={{ marginBottom: 8, fontSize: 11, color: 'var(--muted)' }}>
        Item: <strong style={{ color: 'var(--color-text-primary)' }}>{lines[0].title}</strong>
      </div>
    )
  }
  const toggle = ref => setRefs(prev => prev.includes(ref) ? prev.filter(r => r !== ref) : [...prev, ref])
  return (
    <fieldset style={{ marginBottom: 8, border: 0, padding: 0 }}>
      <legend style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', marginBottom: 4 }}>Which item(s)?</legend>
      {lines.map(line => (
        <label key={line.ref} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '3px 0', cursor: 'pointer' }}>
          <input type="checkbox" checked={refs.includes(line.ref)} onChange={() => toggle(line.ref)} style={{ width: 14, height: 14 }} />
          <span style={{ flex: 1 }}>{line.title}</span>
          <span style={{ color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>£{Number(line.value).toFixed(2)}</span>
        </label>
      ))}
      {!refs.length && <div style={{ fontSize: 11, color: '#b45309' }}>Tick the item the case is about.</div>}
    </fieldset>
  )
}
